import {
  ColumnDataTypes,
  FilterOperators,
  FilterOptionTypes,
  type ColumnConfig,
} from '@/components/DataTable';
import {
  USER_SECURITY_POSTURE_FILTERABLE_FIELDS,
  USER_SECURITY_POSTURE_FILTER_OPTION_FIELDS,
  type UserSecurityPostureDataTableItem,
  type UserSecurityPostureFilterOptionField,
} from '@kirin/api-client/contracts/user-security-posture';
import {
  USP_FILTER_URL_KEYS,
  EMAIL_COLUMN_MAX_WIDTH,
  COVERED_COLUMN_MIN_WIDTH,
  SUPPORTED_COLUMN_MIN_WIDTH,
} from './constants';
import {
  NameCell,
  EmailCell,
  IDEsCoveredCell,
  IDEsSupportedCell,
  MCPCountCell,
  AlertsCell,
  EndpointsCell,
  AgentVersionCell,
  LastActiveCell,
} from './CellRenderers';
import {
  fetchUserSecurityPostureFilterOptions,
  getUserSecurityPostureFilterOptionsQueryKey,
} from './api';
import { ClientFilterOption } from './FilterComponents';
import { getIdeClientDisplayLabel } from '@/components/shared/platform/platform-utils';
import type { TimestampFormat } from '@/contexts/TimestampFormatContext';

// Last Active column width by timestamp format. The absolute UTC string
// (e.g. "September 30, 2025 at 14:45:30 UTC") is much longer than a relative
// delta, so absolute mode needs a wider column to avoid truncation.
const LAST_ACTIVE_WIDTH_RELATIVE = 150;
const LAST_ACTIVE_WIDTH_ABSOLUTE = 200;

const EmptyOperators = [
  { value: FilterOperators.IS_EMPTY, label: 'Is Empty' },
  { value: FilterOperators.IS_NOT_EMPTY, label: 'Is Not Empty' },
];

const EnumOperators = [
  { value: FilterOperators.INCLUDES, label: 'Includes' },
  { value: FilterOperators.EXCLUDES, label: 'Excludes' },
];

const EnumOperatorsWithEmpty = [...EnumOperators, ...EmptyOperators];

const NumberOperators = [
  { value: FilterOperators.INCLUDES, label: 'Includes' },
  { value: FilterOperators.EXCLUDES, label: 'Excludes' },
];

const DateOperators = [
  { value: FilterOperators.GREATER_THAN, label: 'Is After' },
  { value: FilterOperators.LESS_THAN, label: 'Is Before' },
  { value: FilterOperators.BETWEEN, label: 'Is Between' },
];

const DateOperatorsWithEmpty = [...DateOperators, ...EmptyOperators];

/**
 * Create column definitions for the User Security Posture DataTable.
 * organizationId is passed as a parameter so filter option fetchers
 * always use the correct org scope — never stale from a previous render.
 *
 * Column order: Name, Email, IDEs/Platforms (Covered | Supported), # of MCPs,
 * Alerts (Last 7 Days), Kirin Last Active.
 */
export function createUserSecurityPostureColumns(
  organizationId: string,
  onViewClientDetails?: (user: UserSecurityPostureDataTableItem) => void,
  timestampFormat: TimestampFormat = 'relative'
): ColumnConfig<UserSecurityPostureDataTableItem>[] {
  const lastActiveWidth =
    timestampFormat === 'absolute'
      ? LAST_ACTIVE_WIDTH_ABSOLUTE
      : LAST_ACTIVE_WIDTH_RELATIVE;
  return [
    // 1. Name — STRING, dynamic options
    {
      id: USER_SECURITY_POSTURE_FILTERABLE_FIELDS.NAME,
      header: 'Name',
      accessor: 'name',
      minWidth: 150,
      maxWidth: 250,
      renderCell: ({ row }) => <NameCell row={row} />,
      sortable: true,
      sortField: 'name',
      sortKey: 'n',
      filterable: true,
      dataType: ColumnDataTypes.ENUM,
      filterOperators: EnumOperatorsWithEmpty,
      statePersistence: {
        urlKey:
          USP_FILTER_URL_KEYS[USER_SECURITY_POSTURE_FILTERABLE_FIELDS.NAME],
      },
      filterOptions: {
        type: FilterOptionTypes.DYNAMIC,
        pagination: { mode: 'offset', limit: 20 },
        getQueryKey: params =>
          getUserSecurityPostureFilterOptionsQueryKey(
            organizationId,
            USER_SECURITY_POSTURE_FILTERABLE_FIELDS.NAME as UserSecurityPostureFilterOptionField,
            { search: params.search, limit: params.limit }
          ),
        queryFn: params =>
          fetchUserSecurityPostureFilterOptions(
            organizationId,
            USER_SECURITY_POSTURE_FILTERABLE_FIELDS.NAME as UserSecurityPostureFilterOptionField,
            params
          ),
        queryConfig: { staleTime: 2 * 60 * 1000 },
      },
    },

    {
      id: USER_SECURITY_POSTURE_FILTERABLE_FIELDS.EMAIL,
      header: 'Email',
      accessor: 'email',
      minWidth: 140,
      // Cap so Email stops being the greedy width absorber; EmailCell truncates
      // + tooltips, and the freed width flows to Covered / Supported (KIR-3541).
      maxWidth: EMAIL_COLUMN_MAX_WIDTH,
      renderCell: ({ row }) => <EmailCell row={row} />,
      sortable: true,
      sortField: 'email',
      sortKey: 'em',
      filterable: true,
      dataType: ColumnDataTypes.ENUM,
      filterOperators: EnumOperatorsWithEmpty,
      statePersistence: {
        urlKey:
          USP_FILTER_URL_KEYS[USER_SECURITY_POSTURE_FILTERABLE_FIELDS.EMAIL],
      },
      filterOptions: {
        type: FilterOptionTypes.DYNAMIC,
        pagination: { mode: 'offset', limit: 20 },
        getQueryKey: params =>
          getUserSecurityPostureFilterOptionsQueryKey(
            organizationId,
            USER_SECURITY_POSTURE_FILTERABLE_FIELDS.EMAIL as UserSecurityPostureFilterOptionField,
            { search: params.search, limit: params.limit }
          ),
        queryFn: params =>
          fetchUserSecurityPostureFilterOptions(
            organizationId,
            USER_SECURITY_POSTURE_FILTERABLE_FIELDS.EMAIL as UserSecurityPostureFilterOptionField,
            params
          ),
        queryConfig: { staleTime: 2 * 60 * 1000 },
      },
    },

    {
      id: 'idesPlatforms',
      header: 'IDEs/Platforms',
      children: [
        {
          id: USER_SECURITY_POSTURE_FILTERABLE_FIELDS.PLATFORMS_COVERED,
          header: 'Covered',
          headerClassName: 'coverage-header-indent',
          accessor: 'platformsCovered',
          // No maxWidth: stays on the greedy width:100% branch so it consumes
          // freed width and renders badges inline, only collapsing to "+N" on
          // true overflow (KIR-3541). minWidth is the floor for sparse rows.
          minWidth: COVERED_COLUMN_MIN_WIDTH,
          renderCell: ({ row }) => (
            <IDEsCoveredCell
              row={row}
              onViewDetails={() => onViewClientDetails?.(row)}
            />
          ),
          sortable: true,
          sortField: 'platformsCovered',
          sortKey: 'pc',
          filterable: true,
          dataType: ColumnDataTypes.ENUM,
          filterOperators: EnumOperators,
          statePersistence: {
            urlKey:
              USP_FILTER_URL_KEYS[
                USER_SECURITY_POSTURE_FILTERABLE_FIELDS.PLATFORMS_COVERED
              ],
          },
          filterOptions: {
            type: FilterOptionTypes.DYNAMIC,
            getQueryKey: params =>
              getUserSecurityPostureFilterOptionsQueryKey(
                organizationId,
                USER_SECURITY_POSTURE_FILTERABLE_FIELDS.PLATFORMS_COVERED as UserSecurityPostureFilterOptionField,
                { search: params.search, limit: params.limit }
              ),
            queryFn: params =>
              fetchUserSecurityPostureFilterOptions(
                organizationId,
                USER_SECURITY_POSTURE_FILTERABLE_FIELDS.PLATFORMS_COVERED as UserSecurityPostureFilterOptionField,
                params
              ),
            queryConfig: { staleTime: 2 * 60 * 1000 },
            labelFormatter: value => getIdeClientDisplayLabel(value),
            render: (option, isSelected) => (
              <ClientFilterOption
                value={option.value}
                isSelected={isSelected}
              />
            ),
          },
        },
        {
          id: USER_SECURITY_POSTURE_FILTERABLE_FIELDS.PLATFORMS_SUPPORTED,
          header: 'Supported (No Coverage)',
          accessor: 'platformsSupported',
          // No maxWidth: stays on the greedy width:100% branch so it consumes
          // freed width and renders badges inline, only collapsing to "+N" on
          // true overflow (KIR-3541). minWidth is the floor for sparse rows.
          minWidth: SUPPORTED_COLUMN_MIN_WIDTH,
          renderCell: ({ row }) => (
            <IDEsSupportedCell
              row={row}
              onViewDetails={() => onViewClientDetails?.(row)}
            />
          ),
          sortable: true,
          sortField: 'platformsSupported',
          sortKey: 'ps',
          filterable: true,
          dataType: ColumnDataTypes.ENUM,
          filterOperators: EnumOperatorsWithEmpty,
          statePersistence: {
            urlKey:
              USP_FILTER_URL_KEYS[
                USER_SECURITY_POSTURE_FILTERABLE_FIELDS.PLATFORMS_SUPPORTED
              ],
          },
          filterOptions: {
            type: FilterOptionTypes.DYNAMIC,
            getQueryKey: params =>
              getUserSecurityPostureFilterOptionsQueryKey(
                organizationId,
                USER_SECURITY_POSTURE_FILTERABLE_FIELDS.PLATFORMS_SUPPORTED as UserSecurityPostureFilterOptionField,
                { search: params.search, limit: params.limit }
              ),
            queryFn: params =>
              fetchUserSecurityPostureFilterOptions(
                organizationId,
                USER_SECURITY_POSTURE_FILTERABLE_FIELDS.PLATFORMS_SUPPORTED as UserSecurityPostureFilterOptionField,
                params
              ),
            queryConfig: { staleTime: 2 * 60 * 1000 },
            labelFormatter: value => getIdeClientDisplayLabel(value),
            render: (option, isSelected) => (
              <ClientFilterOption
                value={option.value}
                isSelected={isSelected}
              />
            ),
          },
        },
      ],
    },

    {
      id: USER_SECURITY_POSTURE_FILTERABLE_FIELDS.MCP_COUNT,
      header: '# of MCPs',
      accessor: 'mcpCount',
      minWidth: 100,
      maxWidth: 130,
      renderCell: ({ row }) => <MCPCountCell row={row} />,
      sortable: true,
      sortField: 'mcpCount',
      sortKey: 'mc',
      filterable: true,
      dataType: ColumnDataTypes.NUMBER,
      filterOperators: NumberOperators,
      statePersistence: {
        urlKey:
          USP_FILTER_URL_KEYS[
            USER_SECURITY_POSTURE_FILTERABLE_FIELDS.MCP_COUNT
          ],
      },
    },

    {
      id: USER_SECURITY_POSTURE_FILTERABLE_FIELDS.ALERT_COUNT,
      header: 'Alerts (Last 7 Days)',
      accessor: 'alertCount',
      minWidth: 180,
      maxWidth: 240,
      renderCell: ({ row }) => (
        <AlertsCell row={row} organizationId={organizationId} />
      ),
      sortable: true,
      sortField: 'alertCount',
      sortKey: 'ac',
      filterable: true,
      dataType: ColumnDataTypes.NUMBER,
      filterOperators: NumberOperators,
      statePersistence: {
        urlKey:
          USP_FILTER_URL_KEYS[
            USER_SECURITY_POSTURE_FILTERABLE_FIELDS.ALERT_COUNT
          ],
      },
    },

    {
      id: USER_SECURITY_POSTURE_FILTERABLE_FIELDS.LAST_ACTIVE_AT,
      header: 'Kirin Last Active',
      accessor: 'lastActiveAt',
      minWidth: lastActiveWidth,
      maxWidth: 250,
      renderCell: ({ row }) => (
        <LastActiveCell row={row} format={timestampFormat} />
      ),
      sortable: true,
      sortField: 'lastActiveAt',
      sortKey: 'la',
      filterable: true,
      dataType: ColumnDataTypes.DATE,
      filterOperators: DateOperatorsWithEmpty,
      statePersistence: {
        urlKey:
          USP_FILTER_URL_KEYS[
            USER_SECURITY_POSTURE_FILTERABLE_FIELDS.LAST_ACTIVE_AT
          ],
      },
      filterOptions: {
        type: FilterOptionTypes.STATIC,
        options: [],
      },
    },
  ];
}

/**
 * KIR-3035: the "Endpoints" column, inserted (flag-gated) after the Alerts
 * column by the caller. Clicking the count opens the per-user endpoints dialog.
 *
 * The column both **sorts** (by endpoint count — `sortField: 'endpointCount'`)
 * and **filters** (textual multi-select on endpoint NAME — the column `id`
 * `'endpoints'` is the filter field; options come from the distinct-name
 * endpoint). `organizationId` scopes the filter-option fetch.
 */
export function createEndpointsColumn(
  onView: (user: UserSecurityPostureDataTableItem) => void,
  organizationId: string
): ColumnConfig<UserSecurityPostureDataTableItem> {
  return {
    id: USER_SECURITY_POSTURE_FILTERABLE_FIELDS.ENDPOINTS,
    header: 'Endpoints',
    accessor: 'endpointCount',
    minWidth: 110,
    maxWidth: 160,
    renderCell: ({ row }) => <EndpointsCell row={row} onView={onView} />,
    // Sort by the number of endpoints reported per user.
    sortable: true,
    sortField: 'endpointCount',
    sortKey: 'ep',
    // Filter by endpoint name (multi-select of distinct names).
    filterable: true,
    dataType: ColumnDataTypes.ENUM,
    filterOperators: EnumOperators,
    statePersistence: {
      urlKey:
        USP_FILTER_URL_KEYS[USER_SECURITY_POSTURE_FILTERABLE_FIELDS.ENDPOINTS],
    },
    filterOptions: {
      type: FilterOptionTypes.DYNAMIC,
      pagination: { mode: 'offset', limit: 20 },
      getQueryKey: params =>
        getUserSecurityPostureFilterOptionsQueryKey(
          organizationId,
          USER_SECURITY_POSTURE_FILTER_OPTION_FIELDS.ENDPOINTS,
          { search: params.search, limit: params.limit }
        ),
      queryFn: params =>
        fetchUserSecurityPostureFilterOptions(
          organizationId,
          USER_SECURITY_POSTURE_FILTER_OPTION_FIELDS.ENDPOINTS,
          params
        ),
      queryConfig: { staleTime: 2 * 60 * 1000 },
    },
  };
}

/**
 * KIR-3911: the "Agent version" column, inserted (flag-gated) right after the
 * Endpoints column by the caller. Shows the agent version of the user's least
 * up-to-date live endpoint (server-computed `leastAgentVersion`), red when it
 * trails the current release. Display-only — not server-sortable/filterable
 * (the value is a derived aggregate, not a stored column).
 */
export function createAgentVersionColumn(): ColumnConfig<UserSecurityPostureDataTableItem> {
  return {
    id: 'agentVersion',
    header: 'Agent version',
    accessor: 'leastAgentVersion',
    minWidth: 120,
    maxWidth: 160,
    renderCell: ({ row }) => <AgentVersionCell row={row} />,
    sortable: false,
    filterable: false,
  };
}
